/**
 * MarketStatBar — the "stock-exchange" identity strip for the Mango — Farmer
 * Market kit. A compact horizontal bar: a label + an optional grade/as-of meta
 * line on the left, a big current value (display-weight, mirroring StatCard's
 * value treatment), a +/- delta with up/down direction + colour, and a trailing
 * slot for a <Sparkline>.
 *
 * Mirrors StatCard's prop/style conventions: mode-aware MD3 roles via
 * useColorMode + mdSysColor, delta tinted up=primary / down=error /
 * neutral=onSurfaceVariant, no `sx` leak (className pass-through only).
 *
 *   <MarketStatBar
 *     label="Today's mango price"
 *     value={<Money amount={12.5} unit="/ kg" size="xl" />}
 *     delta="+8.5%"
 *     deltaDirection="up"
 *     meta="Grade A · as of 16 Jun 2026"
 *     chart={<Sparkline points={series} />}
 *   />
 */
import { Box } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';

import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor, radius, spacing, typography } from '../theme/tokens.js';
import { Icon } from './Icon.js';
import { Text } from './Text.js';

export interface MarketStatBarProps {
  /** Bar label, e.g. "Today's mango price". */
  label: string;
  /** Big current value — pass a <Money> for the canonical money treatment. */
  value: ReactNode;
  /** Optional secondary meta line under the label (grade, as-of stamp, …). */
  meta?: ReactNode;
  /** Optional leading Material Symbols ligature name. */
  icon?: string;
  /** Optional delta text, e.g. "+8.5%". */
  delta?: string;
  /** Delta direction — tints the delta + picks the trend arrow. Default `neutral`. */
  deltaDirection?: 'up' | 'down' | 'neutral';
  /** Trailing chart slot — pass a <Sparkline>. */
  chart?: ReactNode;
  /** Pass-through className for layout composition. */
  className?: string;
}

// Direction is conveyed with a Unicode triangle glyph rather than an <Icon>:
// the self-hosted icon registry (TD-093) ships no trending_* / arrow_* symbols,
// and an unregistered Icon name renders nothing. The glyph is colour-driven so
// the up/down read survives without an icon dependency.
const DELTA_GLYPH = {
  up: '▲', // ▲
  down: '▼', // ▼
  neutral: '—', // —
} as const;

export const MarketStatBar = forwardRef<HTMLDivElement, MarketStatBarProps>(function MarketStatBar(
  { label, value, meta, icon, delta, deltaDirection = 'neutral', chart, className },
  ref,
) {
  // Mode-aware: a subtle surface-container tile (matches StatCard's neutral tone)
  // so the bar reads as a distinct "ticker" against the page.
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  const deltaColor = {
    up: sys.primary,
    down: sys.error,
    neutral: sys.onSurfaceVariant,
  }[deltaDirection];

  return (
    <Box
      ref={ref}
      className={className}
      sx={{
        backgroundColor: sys.surfaceContainer,
        color: sys.onSurface,
        borderRadius: `${radius.cornerMedium}px`,
        padding: `${spacing.s4}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: `${spacing.s4}px`,
        flexWrap: 'wrap',
      }}
    >
      {/* Left: label + value + delta. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${spacing.xs}px`, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: `${spacing.xs}px` }}>
          {icon ? <Icon name={icon} size={18} color="inherit" /> : null}
          <Text variant="label-medium" color={sys.onSurfaceVariant}>
            {label}
          </Text>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: `${spacing.sm}px`, flexWrap: 'wrap' }}>
          <Box
            sx={{
              fontFamily: typography.fontFamilyDisplay,
              fontWeight: 800,
              fontSize: typography.headlineMedium.fontSize,
              lineHeight: 1.1,
              fontVariantNumeric: 'tabular-nums',
              color: 'inherit',
            }}
          >
            {value}
          </Box>
          {delta ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                color: deltaColor,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <Box component="span" aria-hidden sx={{ fontSize: 12, lineHeight: 1 }}>
                {DELTA_GLYPH[deltaDirection]}
              </Box>
              <Text variant="label-medium" color={deltaColor}>
                {delta}
              </Text>
            </Box>
          ) : null}
        </Box>

        {meta ? (
          <Text variant="label-small" color={sys.onSurfaceVariant}>
            {meta}
          </Text>
        ) : null}
      </Box>

      {/* Right: sparkline slot. */}
      {chart ? <Box sx={{ display: 'flex', alignItems: 'center' }}>{chart}</Box> : null}
    </Box>
  );
});
